import React, { useState } from "react";
import { FaPhone, FaWhatsapp, FaMapMarkerAlt, FaClock } from "react-icons/fa";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    service: "",
    area: "",
  });

  const services = [
    "AC Installation & Uninstallation",
    "AC Repair & Maintenance",
    "Gas Refilling & Service",
    "Split AC Repair",
    "Window AC Repair",
    "Refrigerator Repair",
    "Washing Machine Repair",
    "Microwave Repair",
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const message = `Hi, I need service for ${formData.service}.\nMy name is ${formData.name}.\nLocation: ${formData.area}.`;
    const whatsappUrl = `https://wa.me/918655557727?text=${encodeURIComponent(message)}`;

    window.open(whatsappUrl, "_blank");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Header */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto">
            Get in touch for fast and reliable appliance service
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Info */}
            <div className="space-y-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Reach Out to Us
              </h2>

              {/* Contact Information – Short Text */}
              <div className="bg-white p-5 rounded-2xl shadow-lg">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-blue-600 p-3 rounded-full">
                    <FaPhone className="text-white text-lg" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Door-to-Door Service
                  </h3>
                </div>

                <p className="text-sm text-gray-600 leading-relaxed">
                  Professional appliance repair at your doorstep. No need to
                  move or transport heavy appliances.
                </p>

                <p className="text-sm text-gray-600 mt-2">
                  Share your requirement via WhatsApp or the inquiry form for
                  quick assistance.
                </p>
              </div>

              {/* WhatsApp */}
              <div className="bg-white p-6 rounded-2xl shadow-lg">
                <div className="flex items-center gap-4 mb-4">
                  <div className="bg-green-500 p-4 rounded-full">
                    <FaWhatsapp className="text-white text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      WhatsApp Us
                    </h3>
                    <p className="text-gray-600">
                      Quick and convenient messaging
                    </p>
                  </div>
                </div>
                <a
                  href="https://wa.me/918655557727"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-6 rounded-xl transition-colors duration-200 text-center text-lg"
                  data-testid="contact-whatsapp"
                >
                  💬 Chat on WhatsApp
                </a>
              </div>

              {/* Service Hours */}
              <div className="bg-white p-6 rounded-2xl shadow-lg">
                <div className="flex items-center gap-4 mb-4">
                  <div className="bg-blue-600 p-4 rounded-full">
                    <FaClock className="text-white text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Service Hours
                    </h3>
                    <p className="text-gray-600">We're here when you need us</p>
                  </div>
                </div>
                <div className="space-y-2 text-gray-700">
                  <p className="flex justify-between">
                    <span className="font-medium">All Days:</span>
                    <span>Call for Service</span>
                  </p>
                  <p className="text-red-500 font-bold">
                    🚨 Emergency Service Available
                  </p>
                </div>
              </div>

              {/* Location */}
              <div className="bg-white p-6 rounded-2xl shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-600 p-4 rounded-full">
                    <FaMapMarkerAlt className="text-white text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Service Area
                    </h3>
                    <p className="text-gray-600">
                      Door-to-door service in your local area
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Inquiry Form */}
            <div>
              <div className="bg-white p-8 rounded-2xl shadow-2xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Quick Inquiry Form
                </h2>
                <p className="text-gray-600 mb-6">
                  Fill the form below and we'll contact you via WhatsApp
                </p>

                <form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                  data-testid="contact-form"
                >
                  {/* Name */}
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-gray-700 font-medium mb-2"
                    >
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-600 transition-colors"
                      placeholder="Enter your name"
                      data-testid="form-name-input"
                    />
                  </div>

                  {/* Service */}
                  <div>
                    <label
                      htmlFor="service"
                      className="block text-gray-700 font-medium mb-2"
                    >
                      Service Required *
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-600 transition-colors"
                      data-testid="form-service-select"
                    >
                      <option value="">Select a service</option>
                      {services.map((service, index) => (
                        <option key={index} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Location */}
                  <div>
                    <label
                      htmlFor="area"
                      className="block text-gray-700 font-medium mb-2"
                    >
                      Your Location/Area *
                    </label>
                    <input
                      type="text"
                      id="area"
                      name="area"
                      value={formData.area}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-600 transition-colors"
                      placeholder="Enter your area/location"
                      data-testid="form-area-input"
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-6 rounded-xl transition-colors duration-200 flex items-center justify-center gap-3 text-lg"
                    data-testid="form-submit-button"
                  >
                    <FaWhatsapp className="text-2xl" />
                    Send via WhatsApp
                  </button>

                  <p className="text-sm text-gray-600 text-center">
                    This will open WhatsApp with your inquiry pre-filled
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Urgent Service?
          </h2>
          <p className="text-lg text-blue-100 mb-6">
            Don't wait! Call us now for immediate assistance
          </p>
          <a
            href="tel:8655557727"
            className="inline-block bg-white text-blue-700 px-10 py-5 rounded-full font-bold text-xl shadow-xl hover:shadow-2xl transition-all duration-300"
            data-testid="contact-urgent-call"
          >
            📞 Call +91 8655557727 Now
          </a>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
